import { useRef, useState } from 'react';

import { ModeEditOutlineOutlined } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { Box } from '@mui/system';
import { GridColDef, GridRowId } from '@mui/x-data-grid-pro';
import { useFormikContext } from 'formik';

import { PanelGrid } from 'components/grid';
import { CondicaoPagamento } from 'pages/complementar/condicao-pagamento/types';
import { ConfigPedidoCondicoesPagamentoModal, ConfigPedidoRemoveBtn } from 'pages/compra/configuracao-pedido/components';
import { useConfigPedidoListContext } from 'pages/compra/configuracao-pedido/contexts';
import { CondicaoPagamentoLoja, ConfigPedidoCondicoesPagamento } from 'pages/compra/configuracao-pedido/types';

interface ConfigPedidoCondicoesPagamentoGridProps {
    onRemoveLoja: (cdLoja: number) => void;
    onUpdateLoja: (cdLoja: number | null, condicoesPagamento: CondicaoPagamento[]) => void;
}

const getFilteredRows = (condicoesLojas: CondicaoPagamentoLoja[], valueFilter: string) => {
    return condicoesLojas?.filter((item) => {
        const { cdLoja, dsNomeFantasia, dsRazaoSocial } = item.loja;
        const valueFilterFormatted = valueFilter.trim().toLowerCase();

        return (
            !valueFilter ||
            dsNomeFantasia.toLowerCase().includes(valueFilterFormatted) ||
            dsRazaoSocial?.toLowerCase().includes(valueFilterFormatted) ||
            cdLoja.toString() === valueFilterFormatted
        );
    });
};

const getRows = (condicoesLojas: CondicaoPagamentoLoja[]) => {
    return condicoesLojas?.map((item) => ({
        id: item.loja.cdLoja,
        dsNomeFantasia: item.loja.dsNomeFantasia,
        dsRazaoSocial: item.loja.dsRazaoSocial,
    }));
};

export function ConfigPedidoCondicoesPagamentoGrid({ onRemoveLoja, onUpdateLoja }: ConfigPedidoCondicoesPagamentoGridProps) {
    const { valueFilter, setSelectedRows } = useConfigPedidoListContext();
    const { values } = useFormikContext<ConfigPedidoCondicoesPagamento>();
    const [openModal, setOpenModal] = useState(false);

    const idRow = useRef<number | null>(null);
    const filteredRows = getFilteredRows(values.condicaoPagamentoLoja, valueFilter);
    const rows = getRows(filteredRows);

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Código', width: 80 },
        { field: 'dsNomeFantasia', headerName: 'Nome', flex: 1, minWidth: 100 },
        { field: 'dsRazaoSocial', headerName: 'Razão social', flex: 1, minWidth: 100, sortable: false },
        {
            field: 'acoes',
            align: 'center',
            headerAlign: 'center',
            headerName: 'Ações',
            filterable: false,
            sortable: false,
            hideable: false,
            disableColumnMenu: true,
            minWidth: 70,
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Editar">
                        <span>
                            <IconButton
                                sx={{ p: 0, ml: 0 }}
                                disabled={!values.gatewayPagamento.length}
                                onClick={() => {
                                    idRow.current = row.id;
                                    setOpenModal(true);
                                }}
                            >
                                <ModeEditOutlineOutlined />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <ConfigPedidoRemoveBtn onClick={() => onRemoveLoja(row.id)} />
                </Box>
            ),
        },
    ];

    return (
        <>
            <ConfigPedidoCondicoesPagamentoModal id={idRow.current} open={openModal} setOpen={setOpenModal} onUpdateLoja={onUpdateLoja} />

            <PanelGrid checkboxSelection rows={rows} columns={columns} reload={false} remote={false} loading={false} onClickRow={setSelectedRows} />
        </>
    );
}
